import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getBills } from "../../../../../api/bills";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { isEdgeLocalMode } from "../../../../../lib/edge/edgeState";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { parseMoneyToCents } from "../../../../../utils/money";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { attachedDepositOf, depositCoverageOf } from "../deposits/billDeposit";
import { rememberRefusedFiscalInvoiceBuyer } from "../invoicing/refusedFiscalInvoiceBuyers";
import { runBackgroundBillClose, type CloseBillSelection } from "./backgroundBillClose";
import { useClosingBills } from "./closingBills";
import { billHasActiveRows } from "./tipMath";
import type { BillCloseTip } from "./useBillCloseTip";
import type { CloseDeposit, RecordedBillDeposit } from "./useCloseDeposit";

interface CloseTableSubmitOptions {
  billId: number;
  tableName: string;
  totalAmount: string;
  bill: BillOut | null;
  isRetry: boolean;
  selectedPaymentMethodId: number | null;
  mixedCashAmount: number;
  mixedCardAmount: number;
  cashOnlyMode: boolean;
  closeTip: BillCloseTip;
  closeDeposit: CloseDeposit;
  /** Записаният депозит при повторен опит (иначе null). */
  recordedBillDeposit: RecordedBillDeposit | null;
  /** Фискална фактура при закриване — тялото на `/close` я носи последна;
   *  null — тялото е старото. */
  fiscalInvoice: CloseBillFiscalInvoiceIn | null;
  /** Отстъпка в € на цялата сметка в локален режим с каса, която НЕ я умее
   *  (`capabilities.bill_discount`) — би издала бон по пълни цени, затова
   *  „Закрий" чака облака. */
  billDiscountLocked: boolean;
  onClose: () => void;
}

/**
 * Натискането на „Закриване" в `CloseTableModal` — изнесено от модала по
 * правилото за размера, без промяна в поведението: проверките преди
 * заявката (свежата сума, сменен депозит, бакшиш, „Смесено"), после
 * закриването тръгва във фон (`backgroundBillClose.ts`) и модалът се затваря.
 * `handleCloseRef` е за клавиатурата на модала — Enter вика последния рендер.
 */
export function useCloseTableSubmit({
  billId,
  tableName,
  totalAmount,
  bill,
  isRetry,
  selectedPaymentMethodId,
  mixedCashAmount,
  mixedCardAmount,
  cashOnlyMode,
  closeTip,
  closeDeposit,
  recordedBillDeposit,
  fiscalInvoice,
  billDiscountLocked,
  onClose,
}: CloseTableSubmitOptions) {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.userId);
  const { closingBillIds } = useClosingBills();

  // Само проверката на сумата преди закриването (спинер на бутона) — самото
  // закриване чака принтера ВЪВ ФОН, със спинер на кутийката на масата.
  const [isClosing, setIsClosing] = useState(false);
  const handleCloseRef = useRef<() => void>(() => {});
  // Второ натискане (бърз двоен Enter/клик), докато първото още проверява
  // сумата — state-ът `isClosing` още не е стигнал до handler-а.
  const closeInFlightRef = useRef(false);

  const chosenDeposit = closeDeposit.deposit;
  const depositCoversAll = closeDeposit.coverage?.remainderCents === 0;
  // Фискалната фактура пътува с всяко тяло на `/close`; без нея — старото.
  const fiscalInvoicePart = fiscalInvoice ? { fiscalInvoice } : {};

  handleCloseRef.current = async () => {
    // Връзката е паднала, докато модалът е отворен, а касата не умее
    // отстъпката в € — би издала бон по пълни цени (виж TableFooter).
    if (billDiscountLocked) {
      message.warning(t("itemDiscounts.billCloseOffline"));
      return;
    }
    if (!selectedPaymentMethodId && !depositCoversAll && !closeDeposit.recorded) {
      message.warning(t("closeTable.selectPaymentWarning"));
      return;
    }
    // Закриването на тази сметка още чака принтера (кутийката ѝ се върти) —
    // второ (напр. „Фискализирай" от „Възстановяване" с друг метод) би
    // издало втори бон. Докато модалът блокираше екрана, това не ставаше.
    if (closingBillIds.has(billId)) {
      message.warning(t("closeTable.stillClosing"));
      return;
    }
    if (closeInFlightRef.current) return;
    // Бакшишът: грешка или неприет нов преглед — веднага, без заявка.
    // С депозит бакшиш няма (BE го отказва) — проверките му не важат.
    const tipWarning = chosenDeposit ? null : closeTip.checkBeforeClose();
    if (tipWarning) {
      message.warning(tipWarning);
      return;
    }

    closeInFlightRef.current = true;
    setIsClosing(true);
    try {
      // Подаденият тотал може да е остарял (друг сервитьор, сторно) — при
      // самото натискане теглим свежите сметки. fetchQuery пълни и кеша
      // ["bills"], така че показаната сума се опреснява навсякъде веднага.
      const freshBills = await queryClient.fetchQuery({
        queryKey: ["bills"],
        queryFn: getBills,
        staleTime: 0,
      });
      const freshBill = freshBills.find((bill) => bill.id === billId);
      // Режимът — ЖИВИЯТ, не този от рендера: връзката може да е паднала между
      // купувача/депозита и натискането (и самият GET /bills горе превключва).
      // Касата няма нито фактурата, нито депозита — заявката би станала
      // обикновен бон / бон за цялата сума. Модалът се прерисува в локален
      // режим (лентата на депозита казва какво става), купувачът се помни.
      if (isEdgeLocalMode() && fiscalInvoice) {
        rememberRefusedFiscalInvoiceBuyer(billId, fiscalInvoice);
        message.warning(t("closeTable.fiscalInvoiceOffline"));
        return;
      }
      if (isEdgeLocalMode() && chosenDeposit) {
        message.warning(t("closeTable.deposit.offline"));
        return;
      }
      // Липсва ли сметката в свежия списък, продължаваме със старите данни —
      // BE е авторитетът и връща 409, който се обработва при закриването.
      const freshTotal = parseFloat(freshBill?.total_amount ?? totalAmount);
      if (Math.abs(freshTotal - parseFloat(totalAmount)) > 0.005) {
        message.warning(
          t("closeTable.totalChanged", { total: freshTotal.toFixed(2) }),
        );
        return;
      }
      // Закаченият депозит е сменен/махнат/закачен от друг терминал —
      // показаното в модала вече не е истината (BE би приспаднал друг).
      if (
        !isRetry &&
        freshBill &&
        (attachedDepositOf(freshBill)?.id ?? null) !== (attachedDepositOf(bill)?.id ?? null)
      ) {
        message.warning(t("closeTable.deposit.changed"));
        return;
      }
      if (closeDeposit.blocksZeroBill) {
        message.warning(t("closeTable.deposit.zeroBill"));
        return;
      }

      const backgroundOptions = {
        tableName,
        snapshot: freshBill ?? bill,
        userId: currentUserId,
        dispatch,
        queryClient,
        message,
        notifyPrinterError,
        notifyPrinterTimeout,
        t,
      };

      // Повторен опит на сметка, платена с депозит: празно тяло — BE повтаря
      // бона (Бон 2) със записаните метод и депозит, както „Фискални проблеми".
      if (closeDeposit.recorded && recordedBillDeposit) {
        void runBackgroundBillClose({
          selection: {
            billId,
            paymentMethodId: recordedBillDeposit.paymentMethodId ?? 0,
            retryWithRecordedPayment: true,
          },
          isRetry: true,
          ...backgroundOptions,
        });
        onClose();
        return;
      }

      // Плащане със закачения депозит: покритието се смята наново от СВЕЖАТА
      // сума — покрие ли всичко, методът е „Плащане с депозит"; иначе
      // избраният е за остатъка („Смесено" — сборът му е остатъкът). Без
      // бакшиш и без трите клика.
      if (chosenDeposit) {
        const freshCoverage = depositCoverageOf(
          chosenDeposit.amount,
          parseMoneyToCents(freshBill?.total_amount ?? totalAmount) ?? 0,
        );
        // Без системния метод в списъка BE пак избира „Плащане с депозит",
        // когато депозитът покрива всичко — подаденият метод е без значение.
        const remainderMethodId =
          freshCoverage.remainderCents === 0
            ? (closeDeposit.depositMethodId ?? selectedPaymentMethodId)
            : selectedPaymentMethodId;
        if (!remainderMethodId) {
          message.warning(t("closeTable.selectPaymentWarning"));
          return;
        }
        // Покрие ли депозитът всичко, BE сменя метода на „Плащане с депозит" —
        // суми на „Смесено" няма какво да се проверяват.
        const paysMixed = remainderMethodId === 3 && freshCoverage.remainderCents > 0;
        if (paysMixed) {
          const sum = mixedCashAmount + mixedCardAmount;
          const remainder = freshCoverage.remainderCents / 100;
          if (Math.abs(sum - remainder) > 0.01) {
            message.warning(
              t("closeTable.sumMismatch", {
                total: remainder.toFixed(2),
                entered: sum.toFixed(2),
              }),
            );
            return;
          }
        }
        void runBackgroundBillClose({
          selection: {
            billId,
            paymentMethodId: remainderMethodId,
            ...(paysMixed ? { mixedCashAmount, mixedCardAmount } : {}),
            depositId: chosenDeposit.id,
            ...fiscalInvoicePart,
          },
          isRetry: false,
          ...backgroundOptions,
        });
        onClose();
        return;
      }

      if (selectedPaymentMethodId === 3) {
        const sum = mixedCashAmount + mixedCardAmount;
        if (Math.abs(sum - freshTotal) > 0.01) {
          message.warning(
            t("closeTable.sumMismatch", {
              total: freshTotal.toFixed(2),
              entered: sum.toFixed(2),
            }),
          );
          return;
        }
      }

      // Бакшишът срещу свежото поръчано и свежите настройки; без бакшиш
      // `tip` липсва и тялото е старото.
      const tipOutcome = await closeTip.prepareForClose(
        queryClient,
        parseMoneyToCents(freshBill?.total_amount ?? totalAmount) ?? 0,
        { hasActiveRows: billHasActiveRows(freshBill) },
      );
      if (!tipOutcome.ok) {
        message.warning(tipOutcome.warning);
        return;
      }

      if (!selectedPaymentMethodId) return;
      // Тялото е едно и също за двата пътя — разликата е само endpoint-ът:
      // затворената сметка не може да се затвори втори път.
      const closeSelection: CloseBillSelection = {
        billId,
        paymentMethodId: selectedPaymentMethodId,
        mixedCashAmount:
          selectedPaymentMethodId === 3 ? mixedCashAmount : undefined,
        mixedCardAmount:
          selectedPaymentMethodId === 3 ? mixedCardAmount : undefined,
        total: cashOnlyMode || selectedPaymentMethodId === 4 ? 0 : undefined,
        ...(tipOutcome.tip ? { tip: tipOutcome.tip } : {}),
        ...fiscalInvoicePart,
      };
      // Оттук модалът не чака принтера: закриването тръгва във фон (същата
      // заявка, същите таймаути), спинерът минава на кутийката на масата, а
      // изходът излиза като известие с името ѝ (backgroundBillClose.ts).
      void runBackgroundBillClose({
        selection: closeSelection,
        isRetry,
        // Само при записан бакшиш с карта — иначе опциите са старите.
        ...(isRetry && closeTip.recordedTipWaitsForCard
          ? { retryWaitsForCardTip: true }
          : {}),
        ...backgroundOptions,
      });
      onClose();
    } catch (error) {
      // Тук стига само провалена проверка на сумата (GET /bills, при бакшиш —
      // и свежите му настройки) — нищо не е закрито и модалът остава отворен
      // за повторен опит.
      message.error(getApiErrorMessage(error, t("closeTable.closeError")));
      console.error("Error closing bill:", error);
    } finally {
      closeInFlightRef.current = false;
      setIsClosing(false);
    }
  };

  return {
    isClosing,
    handleCloseRef,
    handleClose: () => handleCloseRef.current(),
  };
}
